import {Controller} from "@hotwired/stimulus"
import {get} from "@rails/request.js";

// Connects to data-controller="pagination"

export default class extends Controller {
  static targets = ["events", "noRecords", "paginationNext"];

  static fetching = false; // debounce

  static values = {
    url: String,
    page: {type: Number, default: 1},
  };

  initialize() {
    this.scroll = this.scroll.bind(this);
  }

  connect() {
    this.previousPage = 0
    document.addEventListener("scroll", this.scroll)
    // Also re-check on resize and on a short interval: the listener only fired on
    // scroll, so when the loaded rows were shorter than the viewport (e.g. the
    // first page of 4 cards) the user could be "at the bottom" without ever
    // scrolling, and later pages never loaded. The interval drains remaining
    // pages until the sentinel is satisfied.
    window.addEventListener("resize", this.scroll)
    this.pollId = setInterval(this.scroll, 600)
    this.scroll()
  }

  disconnect() {
    document.removeEventListener("scroll", this.scroll)
    window.removeEventListener("resize", this.scroll)
    if (this.pollId) clearInterval(this.pollId)
  }

  scroll() {
    let nextPage = parseInt(this.paginationNextTarget.dataset.nextPage)

    if (nextPage > 0) {
      if (this.organizationsEventController) {
        let dateValuePresence = this.organizationsEventController.dateFieldTarget.value == ""
        if (this.organizationsEventController.hasSearchFieldTarget) {
          let searchValuePresence = this.organizationsEventController.searchFieldTarget.value == ""
          if (this.pageEnd && !this.fetching && this.previousPage != nextPage && !this.hasNoRecordsTarget &&
            searchValuePresence && dateValuePresence) {
            this.loadNextPage(nextPage)
          }
        } else if (this.pageEnd && !this.fetching && this.previousPage != nextPage && !this.hasNoRecordsTarget && dateValuePresence) {
          this.loadNextPage(nextPage)
        }
      } else {
        if (this.pageEnd && !this.fetching && this.previousPage != nextPage && !this.hasNoRecordsTarget) {
          this.loadNextPage(nextPage)
        }
      }
    }
  }

  loadNextPage(nextPage) {
    this.eventsTarget.insertAdjacentHTML("beforeend", `
        <div class="col-span-12 container mx-auto h-24 mb-8 spinner" id="spinner">
          <div class="loader">Loading...</div>
        </div>`);

    this.loadRecords(nextPage);
  }

  // Send a turbo-stream request to the controller.
  async loadRecords(nextPage) {
    const url = new URL(this.urlValue);
    url.searchParams.set("page", nextPage);

    this.fetching = true;

    await get(url.href, {
      responseKind: "turbo-stream",
    });

    this.previousPage = nextPage
    this.fetching = false;

    // The turbo-stream just replaced #next_page with the following page's number.
    // Re-check immediately so that when the bottom is still in view (short grid,
    // big viewport) the remaining pages keep loading instead of waiting for
    // another scroll event that may never come.
    this.scroll();
  }

  // Detect if we're at (or near) the bottom of the page. A generous 600px margin
  // means the next page starts loading before the user hits the exact bottom, and
  // also fires when the loaded content is shorter than the viewport (so there's
  // no extra room to scroll into). The old 40px window required scrolling to the
  // pixel-bottom, which silently stranded pages 2+.
  get pageEnd() {
    const {scrollHeight, scrollTop, clientHeight} = document.documentElement;
    return scrollHeight - scrollTop - clientHeight < 600;
  }

  get organizationsEventController() {
    return this.application.controllers.find(
      (controller) => controller.context.identifier === "organization-event"
    );
  }
}
